import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Phone, Mail, Clock, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you within 24 hours.",
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: <Phone className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Phone',
      content: '(240) 355-8003',
      link: 'tel:2403558003'
    },
    {
      icon: <Mail className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Email',
      content: 'info@kaloramamedical.com',
      link: 'mailto:info@kaloramamedical.com'
    },
    {
      icon: <Clock className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Hours',
      content: 'Mon-Fri: 8AM-6PM, Sat: 9AM-4PM',
      link: null
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Contact Us | Kalorama Medical"
        description="Contact Kalorama Medical for all your durable medical equipment needs. Our team is here to help with expert guidance and personalized recommendations."
        keywords="contact medical equipment, medical supplies contact, DME contact"
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2c5282] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Have questions? We're here to help. Reach out to our team today.
          </p>
        </div>
      </div>

      {/* Contact Info Cards */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {contactInfo.map((info, index) => (
              <Card key={index} className="border-2 border-blue-100 hover:border-[#7bb5d4] transition-all duration-300 hover:shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    {info.icon}
                  </div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-2">
                    {info.title}
                  </h3>
                  {info.link ? (
                    <a
                      href={info.link}
                      className="text-gray-600 hover:text-[#7bb5d4] transition-colors duration-200 text-sm"
                    >
                      {info.content}
                    </a>
                  ) : (
                    <p className="text-gray-600 text-sm">{info.content}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Contact Form */}
          <div className="max-w-3xl mx-auto">
            <Card className="border-2 border-blue-100 shadow-xl">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold mb-2 text-[#1e3a5f] text-center">
                  Send Us a Message
                </h2>
                <p className="text-gray-600 mb-8 text-center">
                  Fill out the form below and we'll get back to you as soon as possible
                </p>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="w-full"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="w-full"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject *
                    </label>
                    <Input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="How can we help?"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us more about your needs..."
                      rows={6}
                      className="w-full"
                    />
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-[#1e3a5f] hover:bg-[#2c5282] text-white rounded-full text-lg py-6 transition-all duration-300 hover:scale-105"
                  >
                    Send Message
                    <Send className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Quick Links */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6 text-[#1e3a5f]">
            Looking for Quick Answers?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Check out our frequently asked questions for instant answers to common inquiries
          </p>
          <Button className="bg-[#1e3a5f] hover:bg-[#2c5282] text-white rounded-full px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
            View FAQs
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Contact;
